/**
 * Sewing Floor Board — one sewing floor, today, on a wall. See `design-notes.md`.
 *
 * Two queries: `lines` is the board, `totals` is the headline strip. `totals` is a separate governed
 * view rather than a client-side roll-up because floor efficiency and floor WFT are weighted ratios,
 * `SUM(earned)/SUM(worked)` and not the average of a column of percentages (§4.3).
 *
 * `?kiosk=1` is the shared `useKioskMode` capability, not something this report owns.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { ExportMenu } from '@/components/report/ExportMenu';
import { KioskToggle } from '@/components/report/KioskToggle';
import { exportColumns } from '@/components/report/groupedColumns';
import { useReportSort } from '@/components/report/useReportFilters';
import { useKioskMode } from '@/hooks/useKioskMode';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';

import { BoardHeadline, type BoardTotals } from './cards';
import { BoardLegend, boardColumns, type BoardRow } from './columns';
import { useFittedTier } from './fit';
import {
  METRIC_PARAM,
  METRIC_TABS,
  REFRESH_INTERVAL_MS,
  parseMetric,
  resolveFloor,
  sewingFloorBoardFilters,
} from './filters';
import { useBoardDay } from './useBoardDay';

const REPORT_ID = 'sewing-floor-board';

/**
 * Always-on poll for a board left open on a wall (legacy 120s). Background true so a kiosk or
 * unfocused tab does not freeze — TanStack pauses intervals when the document is hidden otherwise.
 */
const WALL_POLL = {
  staleTime: REFRESH_INTERVAL_MS,
  refetchInterval: REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: true,
} as const;

export default function SewingFloorBoardReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isKiosk } = useKioskMode();

  // replace, not push — Back should leave the report rather than walk back through the metric toggle.
  const setUrlParam = useCallback(
    (name: string, value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Keep the URL on the live board day while following the rule (desk and kiosk both auto-refresh).
  const commitBoardDate = useCallback(
    (date: string) => {
      setUrlParam('tran_date', date);
    },
    [setUrlParam],
  );

  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);
  // The URL may name a floor this factory does not have; resolve to a real pair before querying.
  const selection = resolveFloor(factory, searchParams.get('building'), searchParams.get('floor'));
  const metric = parseMetric(searchParams.get(METRIC_PARAM));
  const tranDate = useBoardDay(searchParams.get('tran_date'), commitBoardDate);

  const schema = useMemo(
    () => sewingFloorBoardFilters(factory, selection),
    [factory, selection],
  );
  // Default sort is the board's own grain, the line code (§4.6); ranking is a header click in the URL.
  const { sort, setSort } = useReportSort();

  const requestFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({
      tran_date: tranDate,
      factory,
      building: selection.building,
      floor: selection.floor,
      metric,
    }),
    [tranDate, factory, selection, metric],
  );

  const lines = useReportQuery<BoardRow>(REPORT_ID, requestFilters, {
    view: 'lines',
    ...WALL_POLL,
  });
  const totals = useReportQuery<BoardTotals>(REPORT_ID, requestFilters, {
    view: 'totals',
    ...WALL_POLL,
  });

  const rows = useMemo(() => lines.data?.rows ?? [], [lines.data]);
  // In kiosk the board must fit with no scroll and no pager, so the density is measured, not guessed.
  const { ref: boardRef, tier } = useFittedTier(isKiosk, rows.length);
  const columns = useMemo(() => boardColumns(tier), [tier]);

  // The three best lines, by the rank the SERVER assigned — ranking them here would derive the answer (§4.3).
  const leaders = useMemo(
    () =>
      rows
        .filter((row) => typeof row['efficiency_rank'] === 'number' && row['efficiency_rank'] <= 3)
        .sort((a, b) => (a['efficiency_rank'] as number) - (b['efficiency_rank'] as number)),
    [rows],
  );

  const request: ReportQueryRequest = { view: 'lines', filters: requestFilters, sort };

  const headline = (
    <BoardHeadline
      totals={totals.data?.rows[0]}
      leaders={leaders}
      isLoading={totals.isLoading}
      tier={tier}
    />
  );

  return (
    <ReportPage
      title="Sewing Floor Board"
      description="Every line on one sewing floor, against its own target for today."
      kiosk={isKiosk}
      // On a wall the floor is what needs naming; which report it is, the reader knows.
      kioskHeading={`${selection.label} · ${metric}`}
      filters={
        isKiosk ? undefined : (
          <FilterBar
            schema={schema}
            afterFilters={
              <ButtonGroupSelector
                label="Measured by"
                options={METRIC_TABS}
                value={metric}
                onChange={(next) => {
                  setUrlParam(METRIC_PARAM, next); // a declared server filter, so it belongs in the URL and the request
                }}
              />
            }
            actions={
              <>
                <ExportMenu
                  reportId={REPORT_ID}
                  request={request}
                  columns={exportColumns(columns)}
                />
                <KioskToggle />
              </>
            }
          />
        )
      }
      summary={headline}
      isLoading={lines.isLoading}
      error={lines.error}
      generatedAt={lines.data?.generatedAt}
      // Null for now — no source here publishes a watermark, and query time is not freshness (§5.7).
      dataAsOf={lines.data?.dataAsOf}
      truncated={lines.data?.truncated === true}
      table={
        <div className="flex min-h-0 flex-1 flex-col">
          {/* The measured box: the height left once the strip, the kiosk bar and the legend have taken theirs. */}
          <div ref={boardRef} className="flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={columns}
            data={rows}
            isLoading={lines.isLoading}
            sort={sort}
            onSortChange={setSort}
            // Board dressing in both modes: no zebra banding, which would fight each row's status colour.
            variant="board"
            // In kiosk, fill the height and drop the pager — a line on page two is a line nobody is watching.
            fitToScreen={isKiosk}
            className={isKiosk ? 'min-h-0 flex-1 border-0 bg-transparent' : undefined}
            caption="Sewing lines on this floor, with efficiency against target and WFT."
            emptyMessage="No line on this floor worked on the selected day. Try another floor or day."
          />
          </div>
          <BoardLegend tier={tier} />
        </div>
      }
    />
  );
}
